import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEnvironmentConfig } from '../config/environments.js';
import { stormAwareFetch } from '../shared/http/stormAwareFetch.js';
import { buildTenantApiBase } from '../shared/tenant.js';
import {
  awaitPlatformMeBootIdentity,
  hasPlatformMeBootOwner,
} from '../shared/session/platformMeBootBridge.js';

/** Break /dev/login ↔ /dev bounce when /me fails (e.g. CORS) while a cookie session looks live. */
const DEV_LOGIN_BOUNCE_GUARD_KEY = 'admin_pwa_cold_dev_login_bounce';

/**
 * Fallback only when AuthProvider is not mounted (unit tests / isolated render).
 * Production `/dev/login` joins AuthProvider cookie-first boot via platformMeBootBridge
 * so logged-out cold start issues at most one GET /api/v1/admin/me (G3).
 * G17 — stormAwareFetch (arm latch + soft-stop; no /me stampede under pause).
 */
async function probePlatformSessionViaMeFallback(): Promise<boolean> {
  try {
    const config = getEnvironmentConfig();
    const apiBase = buildTenantApiBase(config.apiUrl);
    const response = await stormAwareFetch(`${apiBase}/api/v1/admin/me`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Request-ID': crypto.randomUUID(),
      },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Redirect owner for `/dev/login` only.
 *
 * `/` is the public value-prop landing and `/tenants` is the org picker —
 * never auto-redirected (no last-tenant, no token resume, no /dev). Navigation
 * away from those routes happens only when the user chooses a CTA or org row.
 *
 * `/dev/login` remains a hidden platform path (explicit URL only).
 * Install chrome for `/dev*` uses the Dev PWA manifest (see AdminPwaLifecycle).
 *
 * G3 — identity probe is AuthProvider boot `/me` when owner is present.
 * G6 — when Auth owns boot, do **not** cold-navigate sessionLive → `/dev`
 * (LoggedOutOnlyRoute role-gates; avoids wrong-role silent DEV shell hop).
 * Isolated/test fallback (no owner) may still redirect on live `/me`.
 * Bounce guard (`DEV_LOGIN_BOUNCE_GUARD_KEY`) is unchanged.
 */
export function AdminPwaColdStart(): null {
  const location = useLocation();
  const navigate = useNavigate();
  /** Set only after a completed probe for this pathname (survives Strict Mode remount). */
  const handledPathRef = useRef<string | null>(null);

  useEffect(() => {
    const pathname = location.pathname;

    // Public pre-auth surfaces — never hijack.
    if (
      pathname === '/' ||
      pathname === '/tenants' ||
      pathname.startsWith('/tenants/') ||
      pathname === '/register' ||
      pathname.startsWith('/register/')
    ) {
      handledPathRef.current = null;
      return;
    }

    if (pathname !== '/dev/login') {
      handledPathRef.current = null;
      return;
    }
    if (handledPathRef.current === pathname) {
      return;
    }

    let cancelled = false;

    void (async (): Promise<void> => {
      let target: string | null = null;
      try {
        if (sessionStorage.getItem(DEV_LOGIN_BOUNCE_GUARD_KEY) === '1') {
          target = null;
        } else {
          const ownedByAuth = hasPlatformMeBootOwner();
          const sessionLive = ownedByAuth
            ? await awaitPlatformMeBootIdentity()
            : await probePlatformSessionViaMeFallback();
          // G6: when AuthProvider owns boot, LoggedOutOnlyRoute role-gates /dev/login
          // (wrong-role → ?error=invalid_credentials + logout; DEV → /dev). A cold
          // navigate to /dev here raced that carve-out into a silent DEV shell hop.
          if (sessionLive && !ownedByAuth) {
            target = '/dev';
          }
        }
      } catch {
        target = null;
      }

      if (cancelled) {
        return;
      }

      // Mark handled only after probe settles so Strict Mode remount can retry.
      handledPathRef.current = pathname;

      if (target !== null && target !== pathname) {
        try {
          sessionStorage.setItem(DEV_LOGIN_BOUNCE_GUARD_KEY, '1');
        } catch {
          /* ignore */
        }
        navigate(target, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  return null;
}
